<script setup>
definePage({ meta: { layout: 'auth' } });
import useJoin from '@/composables/useJoin';
import { CheckField, PopupModal, Consent } from '@/components/auth';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { refreshToken } from '@/utils/auth';
import { userAgreed } from '@/api/UserInfo';
import { useRnBridge } from '@/composables/useRnBridge';
import { consumeReturnPath } from '@/composables/usePartnerLinkReturn';
const route = useRoute();
const router = useRouter();
const store = useStore();
const { sendMessageToApp } = useRnBridge();
const {
  terms,
  marketingConsent,
  agreeAll,
  toggleAgreeAll,
  toggleMarketingConsent,
  clickConsent,
  showPopup,
  popupMessage,
  popupButtonText,
  showConsent,
  consentHeader,
  consentContent,
  consentButtonText,
  closePopup,
  closeConsent,
} = useJoin();

const pathVal = ref('');

/** 완료 버튼 활성화 여부 */
const isConfirmDisabled = ref(false);

watch(
  () => terms.value.map((term) => term.agreed),
  (newAgreements) => {
    const allAgreed = isSmartwelPartner
      ? terms.value[1]?.agreed && terms.value[2]?.agreed
      : terms.value[0]?.agreed && terms.value[1]?.agreed && terms.value[2]?.agreed;

    isConfirmDisabled.value = allAgreed; // 모두 동의하지 않으면 버튼 비활성화
  },
  { immediate: true }, // 컴포넌트 마운트 시 즉시 체크
);
const hashParams = new URLSearchParams(window.location.hash.substring(1));
// 복지찬스(SmartWel) 제휴 진입 여부 — 약관 2개(이용약관, 개인정보)만 받고 만 14세 약관은 생략
const isSmartwelPartner = hashParams.get('partner') === 'smartwel';

const saveTokens = async (hashParams) => {
  const access_token = hashParams.get('access_token');
  const refresh_token = hashParams.get('refresh_token'); // 주의: refresh_token이 URL에 없을 경우 null
  const id = hashParams.get('id');
  const name = hashParams.get('name');

  const data = {
    access_token: access_token,
    refresh_token: refresh_token,
    id: id,
    name: name,
  };

  // Vuex 스토어에 데이터 전송
  await store.dispatch('user/snslogin', data);
  // refresh_token이 유효한 경우에만 선제 갱신 시도. 실패해도 플로우 중단하지 않음.
  if (refresh_token) {
    try {
      await refreshToken(refresh_token);
    } catch (e) {
      console.warn('[callback] refreshToken 선제 호출 실패 (무시):', e);
    }
  }
};

const onClickConfirm = async () => {
  try {
    await saveTokens(hashParams);
    //01> 동의
    const params = {
      first: false,
      clause: false,
      personal: false,
      mail: false,
      push: false,
      sms: false,
    };

    const agreedTerms = terms.value.map((term, index) => {
      if (index === 0 && term.agreed) {
        params.first = true; // 첫 번째 항목 체크 시 first에 true
      }
      if (index === 1 && term.agreed) {
        params.clause = true; // 두 번째 항목 체크 시 clause에 true
      }
      if (index === 2 && term.agreed) {
        params.personal = true; // 세 번째 항목 체크 시 personal에 true
      }
    });
    if (!isSmartwelPartner && !params.first) {
      alert('만 14세 이상입니다 이용약관에 동의해주세요');
      return;
    }
    if (isSmartwelPartner) {
      // 복지찬스 진입은 first(만 14세) 약관 호출에서 제외
      params.first = false;
    }
    if (!params.clause) {
      alert('서비스 이용약관에 동의해주세요');
      return;
    }
    if (!params.personal) {
      alert('개인정보 이용 및 수집에 동의해주세요');
      return;
    }
    const agreedMarket = marketingConsent.value.map((market, index) => {
      if (index === 0 && market.agreed) {
        params.push = true; // push
      }
      if (index === 1 && market.agreed) {
        params.sms = true; // sms
      }
      if (index === 1 && market.agreed) {
        params.mail = true; // mail
      }
    });
    // 체크된 항목에 대해 API 호출
    const paramsAgree = {};
    let rs = null;
    for (const [key, value] of Object.entries(params)) {
      if (value) {
        paramsAgree.agreed = true;
        try {
          rs = await userAgreed(paramsAgree, key);
        } catch (e) {
          console.error(`[callback] userAgreed(${key}) 실패:`, e);
          throw e;
        }
      }
    }

    if (rs) {
      await saveTokens(hashParams);
      // partner/smartwel 연동 흐름이면 partner 페이지로 자동 복귀 (일반 완료 화면 우회)
      const returnPath = consumeReturnPath();
      if (returnPath) {
        window.location.href = returnPath;
        return;
      }
      await router.push('/auth/finish');
    }
  } catch (error) {
    console.error('[callback] onClickConfirm error:', error?.message, error?.response?.data, error);
    alert('동의 처리 중 오류가 발생했습니다. 다시 시도해 주세요.');
  }
};

// 페이지에 접근했을 때 자동 실행
onMounted(() => {
  // const hashParams = new URLSearchParams(window.location.hash.substring(1))
  const action = hashParams.get('action');

  if (!action) {
    // action이 없을 경우 토큰을 저장하고 partner/smartwel 또는 기본 경로로 이동
    saveTokens(hashParams);
    const returnPath = consumeReturnPath();
    if (returnPath) {
      window.location.href = returnPath;
      return;
    }
    router.push('/saju-input');
  }
});

/** 약관 동의 취소(닫기): 기존 withdraw 플로우 재사용 - 서버 API 호출 없이 로컬 상태만 정리 */
const handleCallbackWithdrawComplete = () => {
  router.replace('/main');
};

const onClickClose = async () => {
  if (window.ReactNativeWebView) {
    sendMessageToApp({ action: 'withdraw' });
    // 이후 앱에서 withdraw echo → useRnMessageGlobal → rn:withdraw 이벤트 → 아래 리스너에서 /main 이동
  } else {
    await store.dispatch('user/logout');
    router.replace('/main');
  }
};

onMounted(() => {
  window.addEventListener('rn:withdraw', handleCallbackWithdrawComplete);
});

onUnmounted(() => {
  window.removeEventListener('rn:withdraw', handleCallbackWithdrawComplete);
});
</script>
<template>
  <div class="auth-page st-callback">
    <div class="auth-page__content">
      <button type="button" class="callback-close" @click="onClickClose" aria-label="닫기">
        ✕
      </button>
      <div class="step-indicator">
        <div class="step-active">
          <div class="step">1</div>
          <div class="line"></div>
          <div class="step active">2</div>
        </div>
      </div>
      <div class="form-title text-center">마지막 단계입니다!<br />약관 내용에 동의해주세요 📋</div>
      <div class="form-description">원활한 서비스 제공을 위해 필수 항목 동의가 필요합니다.</div>

      <div class="terms-container">
        <CheckField
          v-model="agreeAll"
          :class="'terms-header'"
          icon-type="type0"
          label="전체 약관에 동의합니다."
          :toggle-checkbox="toggleAgreeAll"
        />
        <div class="terms-list">
          <CheckField
            v-for="(term, index) in terms"
            v-show="!(isSmartwelPartner && index === 0)"
            :key="index"
            v-model="term.agreed"
            :class="'terms-item'"
            :label="term.text"
            :required-indicator="term.required"
            :optional-indicator="!term.required"
            :show-view-button="index !== 0"
            :toggle-checkbox="index === 3 ? toggleMarketingConsent : () => {}"
            @view="() => clickConsent(term.content, index)"
          />
        </div>
        <div class="marketing-consent">
          <CheckField
            v-for="(item, index) in marketingConsent"
            :key="index"
            v-model="item.agreed"
            :class="'marketing-item'"
            :label="item.text"
          />
        </div>
      </div>
      <button
        class="confirm-btn btn"
        :class="{ active: isConfirmDisabled }"
        :disabled="!isConfirmDisabled"
        @click="onClickConfirm"
      >
        회원가입 완료!
      </button>
    </div>
  </div>
  <Consent
    v-if="showConsent"
    :header="consentHeader"
    :message="consentContent"
    :button-text="consentButtonText"
    @close="closeConsent"
    @confirm="closeConsent"
  />

  <PopupModal
    v-if="showPopup"
    :message="popupMessage"
    :buttonText="popupButtonText"
    :isVisible="showPopup"
    :add-on="joinStep === 2"
    @close="closePopup"
    @confirm="confirmPopup"
  />
</template>
<style lang="scss">
//@use "@nstyles/temporary.scss";

.empty-page {
  width: 100%;
  height: 100vh;
  text-align: center;
  font-size: 30px;
}
.st-callback {
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  .auth-page__content {
    position: relative;
  }
  .callback-close {
    position: absolute;
    top: calc(16px + var(--safe-area-top, 0px));
    right: 16px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    font-size: 24px;
    color: #555;
    cursor: pointer;
  }
}
</style>
